import type { CardColorType } from "@shared/models/card-stack"
import { SECTION_COLOR_MAP } from "@shared/util/colors"
import { mergeAttributes, Node } from "@tiptap/core"
import { ReactNodeViewRenderer } from "@tiptap/react"
import { cancelBackspaceAtNodeStart } from "../../util/cancel-backspace-at-node-start"
import { mergeEmptyCardIntoCardAbove } from "../../util/merge-empty-card-into-card-above"
import { renderStringAsData } from "../../util/render-data"
import { CardTitleComponent } from "./card-title-component"

export interface ExtensionCardTitleAttrs {
  // Card attributes that need to be accessible on the cardTitle.
  // Other card attributes go on the card directly.
  color: CardColorType
}

export const ExtensionCardTitle = Node.create({
  name: "cardTitle",
  content: "text*",
  marks: "",

  // Before heading, so parseHTML gets to our rendered H2's first.
  priority: 200,

  addAttributes() {
    return {
      color: {
        ...renderStringAsData("color"),
        // We need a default here due to the Card schema.
        default: "yellow",
      },
    }
  },

  renderHTML({ node, HTMLAttributes }) {
    const attrs = node.attrs as ExtensionCardTitleAttrs
    return [
      "h2",
      mergeAttributes(HTMLAttributes, {
        "data-content-type": "cardTitle",
        // Give it a color style for pasting into other programs.
        style: `color: ${SECTION_COLOR_MAP[attrs.color]};`,
      }),
      0,
    ]
  },

  parseHTML() {
    return [
      {
        tag: 'h2[data-content-type="cardTitle"]',
      },
    ]
  },

  addKeyboardShortcuts() {
    return {
      Backspace: () =>
        mergeEmptyCardIntoCardAbove(this.editor) ||
        cancelBackspaceAtNodeStart(this.editor, this.name),
      "Shift-Backspace": () => cancelBackspaceAtNodeStart(this.editor, this.name),
      "Mod-Backspace": () => cancelBackspaceAtNodeStart(this.editor, this.name),
      "Alt-Backspace": () => cancelBackspaceAtNodeStart(this.editor, this.name),
    }
  },

  addNodeView() {
    return ReactNodeViewRenderer(CardTitleComponent)
  },
})
